import { Shield, Clock, AlertTriangle, XCircle, ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'

interface Tier {
  icon: React.ReactNode
  label: string
  refund: string
  refundColor: string
  bg: string
  border: string
}

const TIERS: Tier[] = [
  {
    icon: <Shield className="h-4 w-4 text-green-600" />,
    label: 'Mais de 24h de antecedência',
    refund: 'Reembolso integral',
    refundColor: 'text-green-700',
    bg: 'bg-green-50',
    border: 'border-green-200',
  },
  {
    icon: <Clock className="h-4 w-4 text-amber-600" />,
    label: 'Entre 2h e 24h antes',
    refund: 'Reembolso de 50%',
    refundColor: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
  },
  {
    icon: <AlertTriangle className="h-4 w-4 text-orange-600" />,
    label: 'Menos de 2h de antecedência',
    refund: 'Reembolso de 25%',
    refundColor: 'text-orange-700',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
  },
  {
    icon: <XCircle className="h-4 w-4 text-red-500" />,
    label: 'Não comparecimento (no-show)',
    refund: 'Sem reembolso',
    refundColor: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
  },
]

interface CancellationPolicyProps {
  compact?: boolean
  className?: string
}

export function CancellationPolicy({ compact = false, className }: CancellationPolicyProps) {
  if (compact) {
    return (
      <div className={cn('rounded-lg border border-amber-200 bg-amber-50 p-3', className)}>
        <p className="text-xs font-semibold text-amber-800 mb-1.5 flex items-center gap-1.5">
          <Shield className="h-3.5 w-3.5" />
          Política de cancelamento
        </p>
        <ul className="space-y-0.5 text-xs text-amber-700">
          <li>• <strong>+24h antes:</strong> reembolso integral</li>
          <li>• <strong>2h–24h antes:</strong> reembolso de 50%</li>
          <li>• <strong>-2h antes:</strong> reembolso de 25%</li>
          <li>• <strong>Não comparecimento:</strong> sem reembolso</li>
        </ul>
        <Link
          to="/politica-cancelamento"
          className="inline-flex items-center gap-1 text-[11px] text-amber-700 hover:text-amber-900 underline underline-offset-2 mt-2"
        >
          Ver política completa
          <ExternalLink className="h-2.5 w-2.5" />
        </Link>
      </div>
    )
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-center gap-2">
        <Shield className="h-4 w-4 text-gray-500" />
        <h4 className="text-sm font-semibold text-gray-700">Política de cancelamento</h4>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {TIERS.map((tier, i) => (
          <div
            key={i}
            className={cn(
              'flex items-start gap-3 rounded-lg border p-3',
              tier.bg,
              tier.border,
            )}
          >
            <div className="mt-0.5 shrink-0">{tier.icon}</div>
            <div className="min-w-0">
              <p className="text-xs text-gray-600">{tier.label}</p>
              <p className={cn('text-xs font-semibold mt-0.5', tier.refundColor)}>
                {tier.refund}
              </p>
            </div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground">
        Reembolsos são processados automaticamente em até 5 dias úteis.
      </p>
    </div>
  )
}
